/**
 * Icon Helper for Iconify Integration
 * Provides utilities for creating and managing Iconify icons in jQuery environment
 *
 * @module utils/iconHelper
 * @version 1.0.0
 * @created 2026-02-03
 */

let iconifyLoaded = false;
let iconifyLoadPromise = null;

/**
 * Load Iconify script dynamically
 * This should be called early in the application initialization
 * @returns {Promise} Resolves when Iconify is loaded
 */
export function loadIconify() {
    // If already loaded or loading, return the existing promise
    if (iconifyLoadPromise) {
        return iconifyLoadPromise;
    }

    // Check if Iconify is already available (e.g., loaded via HTML)
    if (typeof window !== 'undefined' && window.customElements && window.customElements.get('iconify-icon')) {
        iconifyLoaded = true;
        iconifyLoadPromise = Promise.resolve();
        return iconifyLoadPromise;
    }

    iconifyLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://code.iconify.design/iconify-icon/2.1.0/iconify-icon.min.js';
        script.async = true;

        script.onload = () => {
            iconifyLoaded = true;
            console.log('✨ [ICONIFY] Iconify script loaded successfully');
            resolve();
        };

        script.onerror = () => {
            console.error('❌ [ICONIFY] Failed to load Iconify script');
            reject(new Error('Failed to load Iconify script'));
        };

        document.head.appendChild(script);
    });

    return iconifyLoadPromise;
}

/**
 * Check if Iconify is loaded
 * @returns {boolean}
 */
export function isIconifyLoaded() {
    return iconifyLoaded;
}

/**
 * Create an Iconify icon element
 * @param {string} icon - Iconify icon name (e.g., 'lucide:check')
 * @param {Object} options - Additional options
 * @param {string} options.class - Additional CSS classes
 * @param {string} options.style - Inline styles
 * @param {number|string} options.width - Icon width (default: inherits from CSS)
 * @param {number|string} options.height - Icon height (default: inherits from CSS)
 * @returns {string} HTML string for iconify-icon element
 */
export function createIcon(icon, options = {}) {
    const {
        class: className = '',
        style = '',
        width,
        height
    } = options;

    let attrs = `icon="${icon}" aria-hidden="true"`;

    if (className) {
        attrs += ` class="${className}"`;
    }

    if (style) {
        attrs += ` style="${style}"`;
    }

    if (width) {
        attrs += ` width="${width}"`;
    }

    if (height) {
        attrs += ` height="${height}"`;
    }

    return `<iconify-icon ${attrs}></iconify-icon>`;
}

/**
 * Icon mapping from Font Awesome (efa prefix) to Iconify (Lucide)
 */
export const iconMap = {
    // Edit icons
    'efa-pencil': 'lucide:pencil',
    'efa-edit': 'lucide:edit',
    'efa-solid efa-pencil': 'lucide:pencil',

    // Action icons
    'efa-floppy-disk': 'lucide:save',
    'efa-save': 'lucide:save',
    'efa-paper-plane': 'lucide:send',
    'efa-send': 'lucide:send',

    // List icons
    'efa-list-check': 'lucide:list-checks',
    'efa-th-large': 'lucide:layout-grid',

    // Navigation icons
    'efa-chevron-down': 'lucide:chevron-down',
    'efa-chevron-left': 'lucide:chevron-left',
    'efa-chevron-right': 'lucide:chevron-right',
    'efa-chevron-up': 'lucide:chevron-up',

    // Language icons
    'efa-globe': 'lucide:globe',
    'efa-language': 'lucide:languages',
    'fa-language': 'lucide:languages',

    // Visibility icons
    'efa-eye': 'lucide:eye',
    'efa-eye-slash': 'lucide:eye-off',

    // CRUD icons
    'efa-trash': 'lucide:trash-2',
    'efa-square-plus': 'lucide:plus-square',
    'efa-check': 'lucide:check',
    'efa-xmark': 'lucide:x',
    'efa-times': 'lucide:x',

    // Loading icons
    'efa-arrows-rotate': 'lucide:loader-2',
    'efa-spinner': 'lucide:loader-2',
    'fa-spinner': 'lucide:loader-2',

    // Utility icons
    'efa-gear': 'lucide:settings',
    'efa-settings': 'lucide:settings',
    'efa-info': 'lucide:info',
    'efa-refresh-cw': 'lucide:refresh-cw',
    'efa-external-link': 'lucide:external-link',

    // Sidebar icons
    'efa-gauge-high': 'lucide:gauge',
    'efa-wrench': 'lucide:wrench',
    'efa-user': 'lucide:user',
    'efa-star': 'lucide:star',
};

/**
 * TinyMCE icon mapping from mce-i-* classes to Iconify (Lucide)
 */
export const tinyMCEIconMap = {
    // Basic formatting
    'mce-i-bold': 'lucide:bold',
    'mce-i-italic': 'lucide:italic',
    'mce-i-underline': 'lucide:underline',
    'mce-i-strikethrough': 'lucide:strikethrough',
    'mce-i-subscript': 'lucide:subscript',
    'mce-i-superscript': 'lucide:superscript',

    // Alignment
    'mce-i-alignleft': 'lucide:align-left',
    'mce-i-aligncenter': 'lucide:align-center',
    'mce-i-alignright': 'lucide:align-right',
    'mce-i-alignjustify': 'lucide:align-justify',

    // Lists
    'mce-i-bullist': 'lucide:list',
    'mce-i-numlist': 'lucide:list-ordered',
    'mce-i-indent': 'lucide:indent',
    'mce-i-outdent': 'lucide:outdent',

    // Insert
    'mce-i-link': 'lucide:link',
    'mce-i-unlink': 'lucide:link-2-off',
    'mce-i-image': 'lucide:image',
    'mce-i-media': 'lucide:video',
    'mce-i-table': 'lucide:table',
    'mce-i-hr': 'lucide:minus',
    'mce-i-anchor': 'lucide:anchor',

    // Edit
    'mce-i-undo': 'lucide:undo',
    'mce-i-redo': 'lucide:redo',
    'mce-i-cut': 'lucide:scissors',
    'mce-i-copy': 'lucide:copy',
    'mce-i-paste': 'lucide:clipboard',
    'mce-i-remove': 'lucide:x',

    // View
    'mce-i-fullscreen': 'lucide:maximize',
    'mce-i-preview': 'lucide:eye',
    'mce-i-visualaid': 'lucide:eye-off',

    // Custom plugins (high priority)
    'mce-i-info': 'lucide:info',
    'mce-i-save': 'lucide:save',
    'mce-i-settings': 'lucide:settings',
    'mce-i-code': 'lucide:code',
    'mce-i-css': 'lucide:file-code',

    // Table operations
    'mce-i-tableprops': 'lucide:settings',
    'mce-i-tabledelete': 'lucide:trash-2',
    'mce-i-tableinsertrowbefore': 'lucide:arrow-up-to-line',
    'mce-i-tableinsertrowafter': 'lucide:arrow-down-to-line',
    'mce-i-tabledeleterow': 'lucide:delete',
    'mce-i-tableinsertcolbefore': 'lucide:arrow-left-to-line',
    'mce-i-tableinsertcolafter': 'lucide:arrow-right-to-line',
    'mce-i-tabledeletecol': 'lucide:delete',

    // Colors
    'mce-i-forecolor': 'lucide:palette',
    'mce-i-backcolor': 'lucide:paint-bucket',

    // Advanced
    'mce-i-charmap': 'lucide:hash',
    'mce-i-emoticons': 'lucide:smile',
    'mce-i-nonbreaking': 'lucide:space',
    'mce-i-print': 'lucide:printer',
    'mce-i-help': 'lucide:help-circle',
    'mce-i-plus': 'lucide:plus',
    'mce-i-search': 'lucide:search',
    'mce-i-selectall': 'lucide:check-square',

    // Custom exolog plugins
    'mce-i-followlink': 'lucide:external-link',
    'mce-i-notranslate': 'lucide:languages',
    'mce-i-responsivefilemanager': 'lucide:folder',
    'mce-i-browse': 'lucide:folder-open',
    'mce-i-newtab': 'lucide:external-link',
    'mce-i-caret': 'lucide:chevron-down',
};

/**
 * Get Iconify icon name from Font Awesome class
 * @param {string} faClass - Font Awesome class (e.g., 'efa-pencil' or 'efa efa-pencil')
 * @returns {string} Iconify icon name
 */
export function getIconifyName(faClass) {
    // Clean up the class string
    const cleanClass = faClass
        .replace(/^efa\s+/, '') // Remove leading 'efa '
        .replace(/\s+efa-/, ' efa-') // Normalize spaces
        .trim();

    return iconMap[cleanClass] || iconMap[faClass] || 'lucide:help-circle';
}

/**
 * Replace Font Awesome icon element with Iconify icon
 * Works with jQuery elements
 * @param {jQuery} $element - jQuery element containing the icon
 * @param {string} iconifyIcon - Iconify icon name (e.g., 'lucide:save')
 * @param {Object} options - Additional options for the new icon
 */
export function replaceIcon($element, iconifyIcon, options = {}) {
    const $faIcon = $element.find('i.efa, i.fa, i[class*="efa-"], i[class*="fa-"]');
    if ($faIcon.length > 0) {
        // Extract any non-font-awesome classes to preserve
        const originalClasses = $faIcon.attr('class') || '';
        const preservedClasses = originalClasses
            .split(' ')
            .filter(cls => !cls.startsWith('efa') && !cls.startsWith('fa') && cls !== '')
            .join(' ');

        const finalOptions = {
            ...options,
            class: [options.class, preservedClasses].filter(Boolean).join(' ')
        };

        const iconHtml = createIcon(iconifyIcon, finalOptions);
        $faIcon.replaceWith(iconHtml);
    }
}

/**
 * Update icon attribute on existing Iconify icon element
 * @param {jQuery} $element - jQuery element containing iconify-icon
 * @param {string} newIcon - New Iconify icon name
 */
export function updateIconifyIcon($element, newIcon) {
    const $icon = $element.find('iconify-icon');
    if ($icon.length > 0) {
        $icon.attr('icon', newIcon);
    }
}

export default {
    loadIconify,
    isIconifyLoaded,
    createIcon,
    iconMap,
    tinyMCEIconMap,
    getIconifyName,
    replaceIcon,
    updateIconifyIcon,
};
